import { onMounted, onUnmounted, ref, type Ref } from 'vue';
import { maxWidthQuery, type BreakpointBound, type ShellBreakpoint } from '../types/breakpoints';

/**
 * Resolve a media query **sincronamente**, no setup, quando há `window`.
 *
 * Por que importa: iniciar em `false` e só resolver em `onMounted` faz o primeiro paint sair no
 * formato desktop e trocar para mobile no tick seguinte — um flash de layout em toda navegação de
 * celular (rail aparecendo antes da bottom nav, `<table>` antes dos cartões). O guard de `window`
 * mantém o composable seguro em ambiente sem DOM (SSR, prerender, teste de nó puro).
 */
function resolveMatches(query: string): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia(query).matches;
}

/**
 * Reage a uma media query real via `matchMedia` — substitui o `useMedia`/`useMobile` do protótipo,
 * que hard-codava `(max-width:1000px)` dentro do componente. Aqui a query é sempre passada pelo
 * chamador (tipicamente a partir de `BREAKPOINTS`/`SHELL_BREAKPOINTS`), nunca embutida.
 */
export function useMediaQuery(query: string): Ref<boolean> {
  const matches = ref(resolveMatches(query));
  let mediaQueryList: MediaQueryList | undefined;

  function handleChange(event: MediaQueryListEvent): void {
    matches.value = event.matches;
  }

  onMounted(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    mediaQueryList = window.matchMedia(query);
    // Reavalia: entre o setup e o mount a janela pode ter mudado (hidratação, redimensionamento).
    matches.value = mediaQueryList.matches;
    mediaQueryList.addEventListener('change', handleChange);
  });

  onUnmounted(() => {
    mediaQueryList?.removeEventListener('change', handleChange);
  });

  return matches;
}

/**
 * Atalho para `(max-width: …)` — o caso de uso dominante nos shells dos 3 apps.
 *
 * `bound` decide o comportamento **no** valor: `'upTo'` (padrão) casa em `<= px`; `'below'` casa em
 * `< px`, gerando `px - 0.02` como o CSS exige. Ver `SHELL_BREAKPOINTS` para o limite de cada shell.
 */
export function useBreakpoint(maxWidthPx: number, bound: BreakpointBound = 'upTo'): Ref<boolean> {
  return useMediaQuery(maxWidthQuery(maxWidthPx, bound));
}

/** Forma preferida: recebe o breakpoint nomeado e usa a query já resolvida, sem recalcular limite. */
export function useShellBreakpoint(breakpoint: ShellBreakpoint): Ref<boolean> {
  return useMediaQuery(breakpoint.query);
}
